/* BD PDP fitment buy box — Front/Rear size + qty over BDFitCore.
   Inherits the finder's saved vehicle; direct visitors can pick one inline.
   Cart lines carry the fitment + drill spec as line-item properties. */
(() => {
  const C = window.BDFitCore;
  const DATA = JSON.parse(document.getElementById("bdbb-data").textContent);
  const KEY = C.resolveKey(DATA.apiKey);
  const api = KEY ? C.makeApi(KEY) : null;

  const $ = (id) => document.getElementById(id);
  const els = { vehicle: $("bdbb-vehicle"), finish: $("bdbb-finish"),
    fSize: $("bdbb-front-size"), fQty: $("bdbb-front-qty"),
    rSize: $("bdbb-rear-size"), rQty: $("bdbb-rear-qty"),
    total: $("bdbb-total"), atc: $("bdbb-atc"), msg: $("bdbb-msg") };

  const VARIANTS = DATA.variants;
  // Off-Road series skips the offset check (block emits handle + offroad)
  const GRADE_OPTS = { ignoreOffset: C.isOffroad(DATA) };
  // DP/SDP/FP are manufacturing profiles and LEFT/RIGHT are directional sides —
  // neither is a customer choice. Both collapse into one finish family; the
  // cart splits directional quantities into left/right pairs (back office
  // sorts direction at fulfillment — client decision 2026-08-13).
  const familyOf = (f) => String(f || "").replace(/ (SDP|DP|FP)(?= |$)/, "").replace(/ (LEFT|RIGHT)$/, "").trim();
  const sideOf = (f) => (/(LEFT|RIGHT)$/.exec(String(f || "")) || [])[1] || null;
  const candidatesFor = (size, family) => VARIANTS.filter((v) => v.size === size && familyOf(v.finish) === family);
  // directional product: any variant carries a LEFT/RIGHT side
  const DIRECTIONAL = VARIANTS.some((v) => sideOf(v.finish));
  const RANK = { ok: 0, warn: 1, built: 2, no: 3 };
  // pick the concrete variant behind a (size, family) choice:
  // best fitment for the vehicle when set, else in-stock, else made-to-order.
  // For directional wheels, candidates are restricted to one side (LEFT by
  // default) — the matching other-side variant rides along as `mate`.
  function pickVariant(size, family, axle) {
    let cands = candidatesFor(size, family);
    if (!cands.length) return null;
    const mates = new Map();
    if (DIRECTIONAL) {
      const left = cands.filter((v) => sideOf(v.finish) === "LEFT");
      if (left.length) {
        for (const l of left) {
          const mate = cands.find((v) => v.size === l.size && v.finish === l.finish.replace(/LEFT$/, "RIGHT"));
          if (mate) mates.set(l.id, mate);
        }
        cands = left.filter((l) => mates.has(l.id));
        if (!cands.length) return null;
      }
    }
    const withMate = (res) => {
      if (res && DIRECTIONAL && mates.has(res.variant.id)) res.mate = mates.get(res.variant.id);
      return res;
    };
    const v = vehicle();
    if (v) {
      let best = null;
      for (const cand of cands) {
        const env = (v.axles && v.axles[axle]) || v;
        const g = C.gradeParts(cand.stock, C.parseSize(size), env, v.patterns, v.bore, GRADE_OPTS);
        if (!best || RANK[g.verdict] < RANK[best.g.verdict]) best = { variant: cand, g };
      }
      return withMate(best);
    }
    const pairQty = (x) => DIRECTIONAL && mates.has(x.id) ? Math.min(x.qty, mates.get(x.id).qty) : x.qty;
    const sorted = [...cands].sort((a, b) => (pairQty(b) > 0 ? 1 : 0) - (pairQty(a) > 0 ? 1 : 0)
      || (b.made_to_order ? 1 : 0) - (a.made_to_order ? 1 : 0));
    return withMate({ variant: sorted[0], g: null });
  }
  const bySizeFinish = (size, family) => pickVariant(size, family, "front")?.variant;
  const finishes = [...new Set(VARIANTS.map((v) => familyOf(v.finish)))];
  const sizes = [...new Set(VARIANTS.map((v) => v.size))]
    .sort((a, b) => { const A = C.parseSize(a), B = C.parseSize(b); return A.d - B.d || A.w - B.w; });

  // land on ?variant= links (shared finish selections) with matching state
  const urlVariant = VARIANTS.find((v) => v.id === +(new URLSearchParams(location.search).get("variant") || 0));
  const arrivalParams = new URLSearchParams(location.search);
  const hasArrivalVehicle = ["make", "model", "year"].every(k => arrivalParams.get(k));
  const state = {
    finish: urlVariant ? familyOf(urlVariant.finish) : finishes[0],
    front: urlVariant ? urlVariant.size : sizes[0],
    rear: urlVariant ? urlVariant.size : sizes[0],
    rearTouched: false,
    // Explicit campaign/share vehicle wins over an older saved vehicle.
    saved: hasArrivalVehicle ? null : C.loadVehicle(),
  };
  // campaign deeplinks (?front=&rear=&finish=): land with the set preselected
  {
    const qp = new URLSearchParams(location.search);
    const normV = (s) => String(s || "").toLowerCase().replace(/×/g, "x").trim();
    const qpFront = sizes.find((s) => normV(s) === normV(qp.get("front")));
    const qpRear = sizes.find((s) => normV(s) === normV(qp.get("rear")));
    const qpFinish = finishes.find((f) => normV(f) === normV(qp.get("finish")));
    if (qpFinish) state.finish = qpFinish;
    if (qpFront) state.front = qpFront;
    if (qpRear) { state.rear = qpRear; state.rearTouched = true; }
    state.deeplinked = !!(qpFinish || qpFront || qpRear);
  }
  const vehicle = () => state.saved && state.saved.vehicle;

  // grade a size for one axle in a finish family; null if no vehicle selected
  function fitFor(size, axle, finish = state.finish) {
    const v = vehicle();
    if (!v) return null;
    const picked = pickVariant(size, finish, axle);
    if (!picked) return { verdict: "no", reasons: [`Unavailable in ${finish}`] };
    return picked.g;
  }

  // customer-facing status: availability language, not fitment jargon
  function statusFor(size, axle, finish = state.finish) {
    const picked = pickVariant(size, finish, axle);
    if (!picked) return { key: "unavail", label: `unavailable in ${finish}`, disabled: true };
    const variant = picked.variant;
    // directional pairs are only "in stock" when BOTH sides are on the shelf
    const pairInStock = !picked.mate || picked.mate.qty > 0;
    const g = vehicle() ? picked.g : null;
    if (g) {
      if (g.verdict === "no") return { key: "no", label: "doesn't fit", disabled: true, g };
      // client rule (2026-08-24): ANY fitting part with inventory — drilled or
      // undrilled blank — counts as in stock; "built to order" = fits, nothing
      // on the shelf (FR series), with the build-time note shown below
      if (g.anyStock && pairInStock)
        return { key: "stock", label: "in stock", g };
      if (variant.made_to_order || g.verdict === "built" || !pairInStock)
        return { key: "built", label: "built to order", g };
      return { key: "out", label: "out of stock", disabled: true, g };
    }
    if (variant.qty > 0 && pairInStock) return { key: "stock", label: "in stock" };
    if (variant.made_to_order || (variant.qty > 0 && !pairInStock)) return { key: "built", label: "built to order" };
    return { key: "out", label: "out of stock", disabled: true };
  }

  // a finish "fits" only when BOTH axles have a workable size in it —
  // fronts with no possible rears aren't a set you can put on the car
  const finishFits = (f) =>
    sizes.some((sz) => ["stock", "built"].includes(statusFor(sz, "front", f).key)) &&
    sizes.some((sz) => ["stock", "built"].includes(statusFor(sz, "rear", f).key));

  // If the landed-on finish can't serve this vehicle, move to the first finish
  // with an in-stock fit (else first built-to-order). Returns true if switched.
  function ensureCompatibleSelection() {
    if (!vehicle() || finishFits(state.finish)) return false;
    let builtFallback = null;
    for (const f of finishes) {
      let hasBuilt = false;
      for (const sz of sizes) {
        const s = statusFor(sz, "front", f);
        if (s.key === "stock") { state.finish = f; state.rearTouched = false; return true; }
        if (s.key === "built") hasBuilt = true;
      }
      if (hasBuilt && builtFallback === null) builtFallback = f;
    }
    if (builtFallback !== null) { state.finish = builtFallback; state.rearTouched = false; return true; }
    return false;
  }

  function fillSizeSelect(sel, axle, current) {
    sel.innerHTML = "";
    for (const size of sizes) {
      const s = statusFor(size, axle);
      // sizes not made in this finish are hidden; non-fitting sizes are SHOWN
      // disabled as "Doesn't fit" (client direction 2026-08-24; "— In stock"
      // suffix restored on every in-stock size per client 2026-08-25)
      if (s.key === "unavail") continue;
      const o = document.createElement("option");
      o.value = size;
      o.disabled = !!s.disabled;
      o.textContent = s.key === "stock" ? `${size}" — In stock`
        : s.key === "no" ? `${size}" — Doesn't fit`
        : s.key === "built" ? `${size}" — Built to order`
        : `${size}" — ${s.label}`;
      sel.appendChild(o);
    }
    if (!sel.options.length) {
      const o = document.createElement("option");
      o.value = ""; o.disabled = true; o.textContent = "No compatible sizes";
      sel.appendChild(o);
    }
    // keep selection if still selectable, else first enabled option
    const opts = [...sel.options];
    const keep = opts.find((o) => o.value === current && !o.disabled);
    const first = keep || opts.find((o) => !o.disabled);
    if (first) sel.value = first.value;
    return sel.value;
  }

  function renderVehicleStrip() {
    const v = vehicle();
    if (v) {
      els.vehicle.innerHTML = `<div class="bdbb-veh bdbb-veh--set">
        <div class="bdbb-veh__row">
          <span>Fitment for <b>${v.label}</b></span>
        </div>
        <div class="bdbb-veh__links">
          <button type="button" class="bdbb-link" id="bdbb-change">Change</button>
          <button type="button" class="bdbb-link" id="bdbb-clear">Clear</button>
        </div>
      </div><div class="bdbb-picker" id="bdbb-picker" hidden></div>`;
      C.writeVehicleToUrl(state.saved);   // keep the page URL shareable
      $("bdbb-clear").onclick = () => { C.clearVehicle(); state.saved = null; C.writeVehicleToUrl(null); renderAll(); };
      $("bdbb-change").onclick = () => togglePicker(true);
    } else {
      els.vehicle.innerHTML = `<div class="bdbb-veh bdbb-veh--empty">
        <span>Select your vehicle to verify fitment</span>
        <button type="button" class="bdbb-link" id="bdbb-change">Select vehicle</button>
      </div><div class="bdbb-picker" id="bdbb-picker" hidden></div>`;
      $("bdbb-change").onclick = () => togglePicker(true);
    }
  }
  function togglePicker(showIt) {
    const box = $("bdbb-picker");
    if (!showIt) { box.hidden = true; return; }
    if (!api) { box.hidden = false; box.innerHTML = `<div class="bdbb-err">Fitment lookup not configured (missing API key).</div>`; return; }
    box.hidden = false;
    C.mountPicker(box, { api,
      onVehicle: (v, extra) => {
        if (!v) { box.insertAdjacentHTML("beforeend", `<div class="bdbb-err">No factory data for that vehicle.</div>`); return; }
        state.saved = C.loadVehicle();
        if (ensureCompatibleSelection()) syncGallery();
        renderAll();
      } });
  }

  function money(n) { return `$${n.toFixed(2).replace(/\.00$/, "")}`; }

  // Flag setups that are almost never intended: fronts bigger/wider than
  // rears, or a diameter split beyond 2". Informational — never blocks ATC.
  function renderStaggerCheck(qF, qR) {
    const box = $("bdbb-stagger");
    if (!box) return;
    const F = C.parseSize(state.front), R = C.parseSize(state.rear);
    const seen = [];
    if (qF > 0 && qR > 0 && F && R) {
      if (F.d > R.d) seen.push(`your front diameter (${F.d}&Prime;) is larger than the rear (${R.d}&Prime;)`);
      if (F.w > R.w) seen.push(`your fronts (${F.w}&Prime; wide) are wider than the rears (${R.w}&Prime;)`);
      if (R.d - F.d >= 2) seen.push(`there&rsquo;s a ${R.d - F.d}&Prime; diameter jump between front and rear`);
    }
    if (!seen.length) { box.hidden = true; box.innerHTML = ""; return; }
    box.innerHTML = `<b>Double-check this setup:</b> ${seen.join(", and ")}. `
      + `Most builds run rears the same size or slightly larger. Adjust your selection, `
      + `or <a href="/pages/request-a-quote">contact us</a> and we&rsquo;ll spec it with you.`;
    box.hidden = false;
  }

  // directional wheels sell in left/right pairs — quantities snap to even
  const snapQty = (q) => DIRECTIONAL ? Math.max(0, Math.floor(q / 2) * 2) : q;
  if (DIRECTIONAL) { els.fQty.step = 2; els.rQty.step = 2; }

  function renderTotals() {
    const qF = snapQty(+els.fQty.value || 0), qR = snapQty(+els.rQty.value || 0);
    const vF = pickVariant(state.front, state.finish, "front")?.variant,
          vR = pickVariant(state.rear, state.finish, "rear")?.variant;
    let total = 0, ok = false;
    if (vF && qF) total += vF.price * qF;
    if (vR && qR) total += vR.price * qR;
    ok = (qF > 0 && vF) || (qR > 0 && vR);
    const gF = fitFor(state.front, "front"), gR = fitFor(state.rear, "rear");
    if ((qF && gF && gF.verdict === "no") || (qR && gR && gR.verdict === "no")) ok = false;
    els.total.textContent = total ? `${qF + qR} wheels · ${money(total)}` : "";
    els.atc.disabled = !ok;
    renderStaggerCheck(qF, qR);
    updateAffirm(ok ? total : 0);
    renderBuiltNote(qF, qR);
  }

  // build-time note (client copy, settings-editable): shown whenever a selected
  // axle is built-to-order — nothing on the shelf for that size/finish
  function renderBuiltNote(qF, qR) {
    const el = document.getElementById("bdbb-built-note");
    if (!el || !DATA.builtNote) return;
    const built = (qF > 0 && statusFor(state.front, "front").key === "built")
      || (qR > 0 && statusFor(state.rear, "rear").key === "built");
    el.hidden = !built;
    el.textContent = built ? DATA.builtNote : "";
  }

  // Affirm promotional message ("as low as $X/mo") — tracks the running total.
  // Renders only when the theme's Affirm public key is set (element exists).
  function updateAffirm(total) {
    const el = document.getElementById("bdbb-affirm");
    if (!el) return;
    const cents = Math.round(total * 100);
    el.hidden = cents <= 0;
    el.dataset.amount = String(cents);
    if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === "function") {
      try { window.affirm.ui.refresh(); } catch (_) {}
    }
  }

  // finish circles: the actual wheel render for that finish, clickable
  const familyImage = (f) => (VARIANTS.find((v) => familyOf(v.finish) === f && v.image) || {}).image;
  function renderFinishSwatches() {
    els.finish.innerHTML = "";
    for (const f of finishes) {
      const incompatible = vehicle() && !finishFits(f);
      const b = document.createElement("button");
      b.type = "button";
      b.className = "bdbb-swatch" + (f === state.finish ? " bdbb-swatch--active" : "");
      b.setAttribute("role", "radio");
      b.setAttribute("aria-checked", f === state.finish ? "true" : "false");
      b.disabled = incompatible;
      b.dataset.finish = f;
      const img = familyImage(f);
      b.innerHTML = `<span class="bdbb-swatch__circle">${img ? `<img src="${img}" alt="" loading="lazy">` : ""}</span>
        <span class="bdbb-swatch__name">${f}${incompatible ? " — not compatible" : ""}</span>`;
      els.finish.appendChild(b);
    }
  }

  function renderAll() {
    renderVehicleStrip();
    renderFinishSwatches();
    state.front = fillSizeSelect(els.fSize, "front", state.front);
    state.rear = fillSizeSelect(els.rSize, "rear", state.rearTouched ? state.rear : state.front);
    if (!state.rearTouched) state.rear = els.rSize.value;
    renderTotals();
    els.msg.textContent = "";
    // vehicle concierge: when NO finish of this design serves the saved
    // vehicle, bd-concierge.js offers the shopper their curated page instead
    try {
      const nofit = !!vehicle() && !finishes.some((f) => finishFits(f));
      window.__bdPdpNoFit = nofit;   // late listeners (concierge loads async) read this
      window.dispatchEvent(new CustomEvent("bd:pdp-nofit", { detail: { nofit } }));
    } catch (_) {}
  }

  // Show the picked finish in the media gallery. Horizon renders only the
  // selected variant's media server-side, so we do what its own picker does:
  // re-fetch this section with ?variant= and swap the gallery element.
  let gallerySeq = 0;
  async function syncGallery() {
    const v = pickVariant(state.front, state.finish, "front")?.variant || VARIANTS.find((x) => familyOf(x.finish) === state.finish);
    if (!v) return;
    const gallery = document.querySelector("media-gallery");
    const section = gallery?.closest("[id^='shopify-section-']");
    if (!gallery || !section) return;
    const seq = ++gallerySeq;
    try {
      const sectionId = section.id.replace("shopify-section-", "");
      const res = await fetch(`${location.pathname}?variant=${v.id}&section_id=${encodeURIComponent(sectionId)}`);
      if (!res.ok) return;
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      const fresh = doc.querySelector("media-gallery");
      const current = document.querySelector("media-gallery");
      if (fresh && current && seq === gallerySeq) {
        current.replaceWith(fresh);
        const p = new URLSearchParams(location.search);
        p.set("variant", String(v.id));
        history.replaceState(null, "", `${location.pathname}?${p}`);
      }
    } catch (_) { /* gallery stays on previous media */ }
  }

  els.finish.addEventListener("click", (e) => {
    const b = e.target.closest(".bdbb-swatch");
    if (!b || b.disabled || b.dataset.finish === state.finish) return;
    state.finish = b.dataset.finish;
    state.rearTouched = false;
    renderAll();
    syncGallery();
  });
  els.fSize.addEventListener("change", () => {
    state.front = els.fSize.value;
    if (!state.rearTouched) { state.rear = fillSizeSelect(els.rSize, "rear", state.front); }
    renderTotals();
  });
  els.rSize.addEventListener("change", () => { state.rear = els.rSize.value; state.rearTouched = true; renderTotals(); });
  els.fQty.addEventListener("input", renderTotals);
  els.rQty.addEventListener("input", renderTotals);

  function lineProps(size, axle, position) {
    const props = { "Position": position };
    const v = vehicle();
    const g = fitFor(size, axle);
    if (v && g && g.verdict !== "no") {
      props["Vehicle"] = v.label;
      if (g.pattern) props["Bolt pattern"] = String(g.pattern);
      if (g.offset != null) props["Offset"] = `ET${g.offset}`;
      if (g.hub != null) props["Hub bore"] = `${g.hub} mm${g.ring ? " (hub rings)" : ""}`;
      if (g.verdict === "built") props["Fulfillment"] = "Drilled to order";
      if (g.part) props["_bd_part"] = g.part;
    }
    return props;
  }

  els.atc.onclick = async () => {
    const qF = snapQty(+els.fQty.value || 0), qR = snapQty(+els.rQty.value || 0);
    const pF = pickVariant(state.front, state.finish, "front"),
          pR = pickVariant(state.rear, state.finish, "rear");
    const vF = pF?.variant, vR = pR?.variant;
    const items = [];
    // directional: each axle's quantity splits into equal LEFT/RIGHT lines —
    // direction is sorted by the back office at fulfillment
    const pushAxle = (picked, qty, size, axle, position) => {
      if (!qty || !picked?.variant) return;
      const props = lineProps(size, axle, position);
      if (picked.mate) {
        items.push({ id: picked.variant.id, quantity: qty / 2, properties: { ...props, "Side": "Left" } });
        items.push({ id: picked.mate.id, quantity: qty / 2, properties: { ...props, "Side": "Right" } });
      } else {
        items.push({ id: picked.variant.id, quantity: qty, properties: props });
      }
    };
    pushAxle(pF, qF, state.front, "front", "Front");
    pushAxle(pR, qR, state.rear, "rear", "Rear");
    if (items.length === 2 && items[0].id === items[1].id
        && JSON.stringify({ ...items[0].properties, Position: "" }) === JSON.stringify({ ...items[1].properties, Position: "" })) {
      items[0].quantity += items[1].quantity;
      items[0].properties.Position = "Front & Rear";
      items.pop();
    }
    if (!items.length) return;
    els.atc.disabled = true; els.atc.textContent = "Adding…";
    try {
      const r = await fetch("/cart/add.js", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.description || `Cart error ${r.status}`); }
      window.location.href = "/cart";
    } catch (e) {
      els.msg.textContent = e.message;
      els.atc.disabled = false; els.atc.textContent = "Add set to cart";
    }
  };

  if (ensureCompatibleSelection() || state.deeplinked) syncGallery();
  renderAll();

  // ad/share landings: ?make=&model=&year= selects the vehicle on arrival
  if (api) {
    C.resolveVehicleFromUrl(api).then((resolved) => {
      if (resolved) {
        state.saved = resolved;
        if (ensureCompatibleSelection()) syncGallery();
        renderAll();
      }
    }).catch(() => {});
  }
})();
